import { orgAPI } from "@/api/api";
import type { FileExportKind } from "@/object-actions/fileRecordExportCatalog";
import {
  classifyFileRecord,
  fileRecordDownloadBasename,
  getFileRecordName,
} from "@/panes/file-record-detail/contentType";
import {
  fetchFileRecordTextBody,
  runExportFromFetchedBody,
} from "@/panes/file-record-detail/fileRecordExportRunner";
import type { FileRecord } from "@cohesive/api-client";

export type RemoteFileRecordDownloadFn = (
  downloadUrl: string | undefined,
  fileName?: string,
) => Promise<void>;

export function createRemoteFileRecordExportRunner(
  record: FileRecord,
  download: RemoteFileRecordDownloadFn,
) {
  const kind = classifyFileRecord(record);
  const baseName = fileRecordDownloadBasename(record);
  const displayFileName = getFileRecordName(record) || record.id;

  const isVariantDisabled = (format: FileExportKind): boolean => {
    if (format === "original") {
      return !record.download_url;
    }
    return !record.url;
  };

  const runExport = async (format: FileExportKind) => {
    if (format === "original") {
      // A block's working bytes carry no token definitions; the API's
      // download route bakes them for egress so the saved file opens styled.
      if (record.display?.mode === "block") {
        const blob = await orgAPI.fileRecords.downloadOriginal(record.id);
        const blobUrl = URL.createObjectURL(blob);
        try {
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = displayFileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
        } finally {
          URL.revokeObjectURL(blobUrl);
        }
        return;
      }
      await download(record.download_url, displayFileName);
      return;
    }
    // A block's working bytes carry no token definitions, so every text
    // export (html file, pdf, docx) feeds from the baked egress bytes — the
    // same document a publish produces — instead of the raw body.
    const body =
      record.display?.mode === "block"
        ? await (await orgAPI.fileRecords.downloadOriginal(record.id)).text()
        : await fetchFileRecordTextBody(record);
    await runExportFromFetchedBody({ kind, format, baseName, body });
  };

  return { kind, runExport, isVariantDisabled };
}
